import { useNavigate, useParams } from "react-router-dom";
import { Shield, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loadable, useLoad, Status, ActionButton, can } from "@/components/control/kit";
import { V3Page, V3Aside, Notice } from "@/components/control/v3";

export function OrgRightsMatrix() {
  const st = useLoad("/org-rights");
  return (
    <Loadable state={st}>
      {(d) => (
        <div className="space-y-2.5" data-testid="org-rights-matrix">
          <div className="hidden grid-cols-[1fr_1fr] gap-4 rounded-xl bg-[#EEEAF7] px-4 py-3 text-sm font-semibold text-zinc-600 sm:grid" aria-hidden="true">
            <span>Bereich / Funktion</span><span>Zulässiger Umfang</span>
          </div>
          <ul className="space-y-2.5">
            {d.rights.map((r) => (
              <li key={r.key} className="grid gap-1 rounded-xl border border-[#E6E1F0] bg-white px-4 py-4 sm:grid-cols-[1fr_1fr] sm:gap-4" data-testid={`org-right-${r.key}`}>
                <span className="flex items-center gap-3 font-bold text-brand-purple"><Shield className="h-5 w-5 text-brand-magenta" aria-hidden="true" />{r.label}</span>
                <span className="text-sm text-zinc-700"><span className="sr-only">Zulässiger Umfang: </span>{r.scope}</span>
              </li>
            ))}
          </ul>
          <Notice title="Keine globalen Rechte" text={d.note} testId="org-rights-note" />
        </div>
      )}
    </Loadable>
  );
}

function roleFields(members, locations, labels) {
  return [
    { name: "member_id", label: "Mitglied", type: "select", required: true, options: members.map((m) => ({ value: m.id, label: m.name })) },
    { name: "role", label: "Organisationsrolle", type: "select", required: true, options: Object.entries(labels).map(([value, label]) => ({ value, label })) },
    { name: "location_ids", label: "Standorte (Pflicht für Standortkoordination)", type: "checks", options: locations.map((l) => ({ value: l.id, label: l.name })), default: [] },
    { name: "reason", label: "Begründung", type: "textarea", required: true },
  ];
}

function OrgRoles({ orgId }) {
  const { user } = useAuth();
  const st = useLoad(`/organizations/${orgId}/roles`);
  const locs = useLoad(`/organizations/${orgId}/locations`);
  const members = useLoad(can(user, "members:view") ? "/members?status=aktiv" : null);
  const manage = can(user, "organizations:approve");
  return (
    <Loadable state={st}>
      {(d) => (
        <div className="space-y-2.5 pt-4" data-testid="org-roles">
          <h2 className="text-lg font-bold text-brand-purple">Bestätigte Organisationsrollen</h2>
          {d.roles.length === 0 && <p className="text-sm text-zinc-600" data-testid="org-roles-empty">Noch keine Organisationsrollen vergeben.</p>}
          {d.roles.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E6E1F0] bg-white px-4 py-3" data-testid={`org-role-${r.id}`}>
              <span><span className="block font-semibold text-brand-purple">{r.member_name}</span>
                <span className="text-sm text-zinc-600">{r.role_label}{r.location_ids.length ? ` · ${r.location_ids.length} Standort(e)` : ""}</span></span>
              <span className="flex items-center gap-2"><Status value={r.status} />
                {manage && r.status === "bestätigt" && <ActionButton label="Widerrufen" icon={Ban} testId={`revoke-org-role-${r.id}`} danger submitLabel="Widerrufen" onDone={st.reload}
                  fields={[{ name: "reason", label: "Begründung", type: "textarea", required: true }]}
                  onSubmit={async (v) => { await api.post(`/org-roles/${r.id}/revoke`, v); return "Rolle widerrufen."; }} />}</span>
            </div>
          ))}
          {manage && members.data && locs.data && (
            <ActionButton label="Organisationsrolle zuweisen" variant="default" testId="assign-org-role" submitLabel="Rolle zuweisen" onDone={st.reload}
              description="Die Rolle gilt nur innerhalb dieser Organisation. Persönliche Passfreigaben sind nicht enthalten."
              fields={roleFields(members.data.members || [], locs.data.locations, d.role_labels)}
              onSubmit={async (v) => { await api.post(`/organizations/${orgId}/roles`, v); return "Organisationsrolle zugewiesen."; }} />
          )}
        </div>
      )}
    </Loadable>
  );
}

export default function OrgRights() {
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <V3Page crumb={id ? "Organisationen" : "Team & Rechte"} title="Organisationsrechte im Vergleich"
      subtitle="Vergleiche Organisationsrechte ohne globale Verwaltungsrechte zu vergeben."
      aside={<V3Aside subject="Eigene Organisation · Zugewiesene Standorte" />} testId="org-rights-page">
      <OrgRightsMatrix />
      {id && <OrgRoles orgId={id} />}
      <div className="pt-2">
        <button type="button" onClick={() => navigate(id ? `/organisationen/${id}` : -1)} data-testid="org-rights-back"
          className="h-11 rounded-lg border border-[#E6E1F0] bg-white px-6 text-base font-bold text-brand-purple">Zurück</button>
      </div>
    </V3Page>
  );
}
